import {useNavigate, useParams} from "react-router-dom";
import {HStack, Input, Spinner, Table, TableContainer, Tbody, Text, useToast, VStack} from "@chakra-ui/react";
import {useQuery} from "@tanstack/react-query";
import {editUserNew, getUsersNewById} from "../../../../data/api/Customer";
import {getData} from "../../../../utils/Utils";
import {SetImageModel, UserModelNew, UserModelQueryParamsNew} from "../../../../data/model/Producer";
import {Resolver, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {userSchema} from "../../../../data/schema/productSchema";
import {AddTableRow} from "../../../../components/atoms/AddTableRow/AddTableRow";
import {BaseContentPage} from "../../../../components/molecules/BaseContentPage/BaseContentPage";
import {CheckCircleIcon} from "@chakra-ui/icons";
import {scrollbarCss} from "../../../../theme/theme";
import * as React from "react";
import {useEffect, useState} from "react";
import {ImageSelectorComponent} from "../../../../components/molecules/ImageSelectorComponent/ImageSelectorComponent";

export const EditUserPage = () => {
    const navigate = useNavigate();
    const toast = useToast();

    const {id} = useParams();

    const {data, refetch} = useQuery({
        queryKey: ["get-user", id],
        enabled: !!id,
        queryFn: () => getUsersNewById({id: id || ""}),
        refetchOnWindowFocus: "always",
        staleTime: 0
    });

    const user = getData<UserModelNew>(data);
    const [mainImage, setMainImage] = useState<SetImageModel | undefined>()

    const methods = useForm<UserModelQueryParamsNew>({
        mode: "all",
        resolver: yupResolver(userSchema) as Resolver<UserModelQueryParamsNew>
    });

    const {
        register,
        handleSubmit,
        setValue,
        formState: {
            errors,
            isSubmitting
        }
    } = methods;

    const {
        firstName,
        lastName,
        email,
        streetAddress,
        city,
        addressNumber,
        cap,
        phone,
        orderNote,
        userProfileImage
    } = user || {};

    const [isDelete, setIsDelete] = useState(false);

    useEffect(() => {
        user?.userProfileImage && setMainImage({imageUrl: userProfileImage});
    }, [user?.userProfileImage]);

    const onSubmit = async (user: UserModelQueryParamsNew) => {
        await editUserNew(id!, {...user, userProfileImage: isDelete ? "delete" : mainImage?.imageBase64}).then(() => {
            refetch().then(() => navigate("/users", {replace: true}))
        }).catch(reason => {
            toast({
                title: "Si è verificato un errore",
                description: reason?.message ?? "-"
            });
        });
    }

    function getInputRow({title, fieldName, description, defaultValue, hintText}: {
        title: string,
        fieldName: any,
        defaultValue?: string,
        description?: string,
        hintText?: string,
    }) {
        return <AddTableRow name={title}>
            <VStack alignItems={"flex-start"} w={"100%"}>
                <Input
                    inputMode={"text"}
                    colorScheme={"primary"}
                    variant='filled'
                    _placeholder={{fontSize: 14}}
                    defaultValue={defaultValue}
                    disabled={isSubmitting}
                    {...register(fieldName)}
                    placeholder={hintText}/>
                {description && <Text fontSize={"14px"} color={"rgba(0,0,0,0.6)"}>{description}</Text>}
            </VStack>
        </AddTableRow>;
    }

    return <BaseContentPage>
        <VStack id={"container"} w={'100%'} h={'100%'} alignItems={'center'}>
            <HStack cursor={"pointer"} h={"64px"} px={"24px"} borderRadius={"24px"} position={"absolute"}
                    alignItems={"center"} zIndex={5}
                    justifyContent={"center"} bottom={"24px"} right={"48px"}
                    backgroundColor={isSubmitting ? "primary.100" : "secondary.200"}
                    _hover={{backgroundColor: "tertiary.200"}} onClick={handleSubmit(onSubmit)}>
                {isSubmitting ? <Spinner width={"24px"} height={"24px"}/> :
                    <CheckCircleIcon w={"24px"} h={"24px"} color={"primary.400"} alignSelf={"center"}/>}
                <Text fontWeight={"bold"} color={"primary.400"}>Conferma</Text>
            </HStack>

            <Text fontSize={"24px"} justifySelf={"center"} fontWeight={"bold"} ms={"24px"}>Modifica utente</Text>

            <TableContainer w={"100%"} pb={"86px"} alignItems={"center"} whiteSpace={"pre-line"} mr={"16px"}
                            overflowY={"scroll"}
                            css={scrollbarCss({marginRight: 48})}>
                <Table variant={"none"} w={"70%"} mx={"auto"}>
                    <Tbody>
                        {getInputRow({
                            title: "Nome",
                            fieldName: "firstName",
                            defaultValue: firstName
                        })}
                        {getInputRow({
                            title: "Cognome",
                            fieldName: "lastName",
                            defaultValue: lastName
                        })}
                        {getInputRow({
                            title: "Email",
                            fieldName: "email",
                            defaultValue: email
                        })}
                        {getInputRow({
                            title: "Indirizzo",
                            fieldName: "streetAddress",
                            defaultValue: streetAddress
                        })}
                        {getInputRow({
                            title: "Numero civico",
                            fieldName: "addressNumber",
                            defaultValue: addressNumber
                        })}
                        {getInputRow({
                            title: "Città",
                            fieldName: "city",
                            defaultValue: city
                        })}
                        {getInputRow({
                            title: "CAP",
                            fieldName: "cap",
                            defaultValue: cap
                        })}
                        {getInputRow({
                            title: "Telefono",
                            fieldName: "phone",
                            defaultValue: phone
                        })}
                        {getInputRow({
                            title: "Note ordini",
                            fieldName: "orderNote",
                            defaultValue: orderNote
                        })}
                        <AddTableRow name={"Immagine"}>
                            <ImageSelectorComponent
                                setImageModel={setMainImage}
                                imageModel={mainImage}
                                handleCloseButtonClick={() => {
                                    setIsDelete(true)
                                }}
                                isMain={true}/>
                        </AddTableRow>
                    </Tbody>
                </Table>
            </TableContainer>
        </VStack>
    </BaseContentPage>
}
